import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Download, Filter, RefreshCw, AlertCircle, Users, FileText,
  DollarSign, TrendingUp, TrendingDown, Minus, ShoppingCart, FileSpreadsheet
} from 'lucide-react';
import { reportesApi } from '../../utils/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const MESES = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

export default function VentasCliente() {
  const currentDate = new Date();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [mes, setMes] = useState(currentDate.getMonth() + 1);
  const [anio, setAnio] = useState(currentDate.getFullYear());

  const aniosDisponibles = [];
  for (let i = 0; i < 5; i++) {
    aniosDisponibles.push(currentDate.getFullYear() - i);
  }

  const cargarReporte = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportesApi.getVentasCliente({ mes, anio });
      setData(response.data.data);
    } catch (err) {
      console.error('Error cargando reporte:', err);
      setError(err.response?.data?.error || 'Error al cargar el reporte');
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const response = await reportesApi.exportVentasCliente({ mes, anio });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ventas_por_Cliente_${MESES[mes-1].label}_${anio}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte exportado exitosamente');
    } catch (err) {
      console.error('Error exportando:', err);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  const exportarPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await reportesApi.exportVentasClientePdf({ mes, anio });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ventas_por_Cliente_${MESES[mes-1].label}_${anio}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF exportado exitosamente');
    } catch (err) {
      console.error('Error exportando PDF:', err);
      toast.error('Error al exportar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    cargarReporte();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const TendenciaIcon = ({ tendencia }) => {
    if (tendencia === 'up') return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (tendencia === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Ventas por Cliente</h1>
          <p className="text-gray-500 mt-1">Análisis de ventas con ranking y tendencias</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportarPdf}
            disabled={exportingPdf || !data || data.clientes?.length === 0}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
              exportingPdf || !data || data.clientes?.length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-red-500 text-red-600 hover:bg-red-50'
            )}
          >
            {exportingPdf ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            PDF
          </button>
          <button
            onClick={exportarExcel}
            disabled={exporting || !data || data.clientes?.length === 0}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
              exporting || !data || data.clientes?.length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-green-500 text-green-600 hover:bg-green-50'
            )}
          >
            {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
            <select
              value={mes}
              onChange={(e) => setMes(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
            <select
              value={anio}
              onChange={(e) => setAnio(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <button
            onClick={cargarReporte}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
            Consultar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Ventas</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(data.resumen.totalVentas)}
                  </p>
                  <div className="flex items-center gap-1 mt-2">
                    <TendenciaIcon tendencia={data.resumen.tendencia} />
                    <span className={clsx(
                      'text-sm font-medium',
                      data.resumen.variacionPorcentaje > 0 ? 'text-green-600' :
                      data.resumen.variacionPorcentaje < 0 ? 'text-red-600' : 'text-gray-500'
                    )}>
                      {formatPercent(data.resumen.variacionPorcentaje)} vs mes anterior
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clientes Activos</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{data.resumen.totalClientes}</p>
                  <p className="text-xs text-gray-400 mt-2">con compras este mes</p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <Users className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Facturas</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{data.resumen.totalFacturas}</p>
                  <p className="text-xs text-gray-400 mt-2">documentos emitidos</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <FileText className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Ticket Promedio</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {formatCurrency(data.resumen.ticketPromedio)}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">por factura</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Top 5 + Concentración */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Top 5 Clientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 5 Clientes</h3>
              <div className="space-y-3">
                {data.top5.map((cliente, index) => {
                  const maxTotal = data.top5[0]?.total || 1;
                  const percentage = (cliente.total / maxTotal) * 100;
                  const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500'];

                  return (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">{cliente.nombre}</span>
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(cliente.total)}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={clsx('h-full rounded-full transition-all', colors[index])}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Concentración */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Concentración</h3>
              <div className="flex flex-col items-center justify-center h-[calc(100%-2rem)]">
                <div className="relative w-32 h-32">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="56" stroke="#E5E7EB" strokeWidth="12" fill="none" />
                    <circle
                      cx="64" cy="64" r="56"
                      stroke="#3B82F6" strokeWidth="12" fill="none"
                      strokeDasharray={`${data.participacionTop5 * 3.52} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-900">{data.participacionTop5.toFixed(0)}%</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4 text-center">
                  Los Top 5 representan el {data.participacionTop5.toFixed(1)}% de las ventas
                </p>
              </div>
            </motion.div>
          </div>

          {/* Tabla de Clientes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100"
          >
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Detalle por Cliente</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="text-left text-sm text-gray-500">
                    <th className="px-4 py-3 font-medium">#</th>
                    <th className="px-4 py-3 font-medium">Cliente</th>
                    <th className="px-4 py-3 font-medium">Cédula</th>
                    <th className="px-4 py-3 font-medium text-center">Facturas</th>
                    <th className="px-4 py-3 font-medium text-right">Total</th>
                    <th className="px-4 py-3 font-medium text-right">Ticket Prom.</th>
                    <th className="px-4 py-3 font-medium text-center">Var. vs Anterior</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.clientes.map((cliente) => (
                    <tr key={cliente.clienteId} className="text-sm hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className={clsx(
                          'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                          cliente.ranking <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                        )}>
                          {cliente.ranking}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{cliente.clienteNombre}</td>
                      <td className="px-4 py-3 text-gray-600">{cliente.clienteIdentificacion}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{cliente.totalFacturas}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {formatCurrency(cliente.totalVentas)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {formatCurrency(cliente.ticketPromedio)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <TendenciaIcon tendencia={cliente.tendencia} />
                          <span className={clsx(
                            'text-sm font-medium',
                            cliente.variacionPorcentaje > 0 ? 'text-green-600' :
                            cliente.variacionPorcentaje < 0 ? 'text-red-600' : 'text-gray-500'
                          )}>
                            {formatPercent(cliente.variacionPorcentaje)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.clientes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No hay datos para el período seleccionado
              </div>
            )}
          </motion.div>
        </>
      )}
    </div>
  );
}
